<script setup lang="ts">
  import { reactive, withDefaults, watch, computed } from 'vue';
  import isEqual from 'lodash/isEqual';

  interface Props {
    value?: {
      allowedDays: number[];
      allowedHours: Array<{ from: string; to: string }>;
    };
  }

  const props = withDefaults(defineProps<Props>(), {
    value: () => ({
      allowedDays: [],
      allowedHours: [],
    }),
  });
  const emit = defineEmits(['input']);

  // Local copy of data
  const localData = reactive({
    allowedDays: [...props.value.allowedDays],
    allowedHours: [...props.value.allowedHours],
  });

  // Watch prop changes and update local data
  watch(
    () => props.value,
    (newValue) => {
      // Only update if values are actually different
      if (!isEqual(newValue.allowedDays, localData.allowedDays)) {
        localData.allowedDays = [...newValue.allowedDays];
      }
      if (!isEqual(newValue.allowedHours, localData.allowedHours)) {
        localData.allowedHours = [...newValue.allowedHours];
      }
    },
    { deep: true }
  );

  // Watch local data changes and emit to parent
  watch(
    localData,
    (newValue) => {
      // Only emit if values are actually different from props
      if (
        !isEqual(newValue.allowedDays, props.value.allowedDays) ||
        !isEqual(newValue.allowedHours, props.value.allowedHours)
      ) {
        emit('input', {
          allowedDays: [...newValue.allowedDays],
          allowedHours: [...newValue.allowedHours],
        });
      }
    },
    { deep: true }
  );

  // Days of the week
  const daysOfWeek = [
    { value: 0, text: 'Domingo' },
    { value: 1, text: 'Lunes' },
    { value: 2, text: 'Martes' },
    { value: 3, text: 'Miércoles' },
    { value: 4, text: 'Jueves' },
    { value: 5, text: 'Viernes' },
    { value: 6, text: 'Sábado' },
  ];

  // Hour validation rules
  const timeRules = {
    from: [
      (v: string, index: number) => {
        if (!v) return 'La hora de inicio es requerida';
        const to = localData.allowedHours[index]?.to;
        if (to && v >= to) {
          return 'La hora de inicio debe ser anterior a la hora de fin';
        }
        return true;
      },
    ],
    to: [
      (v: string, index: number) => {
        if (!v) return 'La hora de fin es requerida';
        const from = localData.allowedHours[index]?.from;
        if (from && v <= from) {
          return 'La hora de fin debe ser posterior a la hora de inicio';
        }
        return true;
      },
    ],
  };

  function addTimeRange() {
    localData.allowedHours.push({ from: '09:00', to: '21:00' });
  }

  function removeTimeRange(index: number) {
    localData.allowedHours.splice(index, 1);
  }

  function selectAllDays() {
    localData.allowedDays = daysOfWeek.map((d) => d.value);
  }

  function clearAllDays() {
    localData.allowedDays = [];
  }

  function selectWeekdays() {
    localData.allowedDays = [1, 2, 3, 4, 5];
  }

  function selectWeekend() {
    localData.allowedDays = [0, 6];
  }

  const hasSelectedDays = computed(() => localData.allowedDays.length > 0);
  const hasTimeRanges = computed(() => localData.allowedHours.length > 0);
</script>

<template>
  <div class="v2-modal-coupon-schedule">
    <!-- Days of week -->
    <div class="section">
      <h4 class="section-title">
        <v-icon small left>mdi-calendar-week</v-icon>
        Días permitidos
      </h4>

      <div class="quick-actions mb-3">
        <v-btn x-small outlined class="mr-2" @click="selectAllDays">
          Todos
        </v-btn>
        <v-btn x-small outlined class="mr-2" @click="selectWeekdays">
          Lun-Vie
        </v-btn>
        <v-btn x-small outlined class="mr-2" @click="selectWeekend">
          Fin de semana
        </v-btn>
        <v-btn x-small outlined @click="clearAllDays"> Limpiar </v-btn>
      </div>

      <div class="days-grid">
        <v-checkbox
          v-for="day in daysOfWeek"
          :key="day.value"
          v-model="localData.allowedDays"
          :label="day.text"
          :value="day.value"
          hide-details
          class="mb-2"
        />
      </div>

      <p class="text-caption text--secondary mt-2">
        Si no selecciona ningún día, el cupón será válido todos los días
      </p>
    </div>

    <v-divider class="my-4" />

    <!-- Time ranges -->
    <div class="section">
      <div class="section-header">
        <h4 class="section-title">
          <v-icon small left>mdi-clock-outline</v-icon>
          Horarios permitidos
        </h4>

        <v-btn small color="primary" outlined @click="addTimeRange">
          <v-icon small left>mdi-plus</v-icon>
          Agregar horario
        </v-btn>
      </div>

      <div v-if="hasTimeRanges" class="time-ranges">
        <v-row
          v-for="(range, index) in localData.allowedHours"
          :key="index"
          class="time-range-row"
        >
          <v-col cols="5">
            <v-text-field
              v-model="range.from"
              label="Desde"
              type="time"
              :rules="[(v) => timeRules.from[0](v, index)]"
              outlined
              dense
            />
          </v-col>

          <v-col cols="5">
            <v-text-field
              v-model="range.to"
              label="Hasta"
              type="time"
              :rules="[(v) => timeRules.to[0](v, index)]"
              outlined
              dense
            />
          </v-col>

          <v-col cols="2" class="d-flex align-center">
            <v-btn icon small color="error" @click="removeTimeRange(index)">
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <p v-else class="text-caption text--secondary">
        No hay restricciones de horario. Haga clic en "Agregar horario" para
        añadir una.
      </p>

      <p class="text-caption text--secondary mt-2">
        Si no configuras horarios, el cupón será válido las 24 horas
      </p>
    </div>

    <!-- Schedule preview -->
    <v-divider class="my-4" />

    <div class="section">
      <h4 class="section-title">
        <v-icon small left>mdi-calendar-check</v-icon>
        Vista previa del horario
      </h4>

      <v-alert
        v-if="!hasSelectedDays && !hasTimeRanges"
        type="info"
        outlined
        dense
      >
        El cupón será válido todos los días, las 24 horas
      </v-alert>

      <v-alert v-else type="success" outlined dense>
        <div class="schedule-preview">
          <div v-if="hasSelectedDays">
            <strong>Días:</strong>
            {{
              localData.allowedDays
                .map((d) => daysOfWeek.find((day) => day.value === d)?.text)
                .join(', ')
            }}
          </div>
          <div v-else><strong>Días:</strong> Todos los días</div>

          <div v-if="hasTimeRanges" class="mt-1">
            <strong>Horarios:</strong>
            <span v-for="(range, index) in localData.allowedHours" :key="index">
              {{ range.from }} - {{ range.to
              }}<span v-if="index < localData.allowedHours.length - 1">, </span>
            </span>
          </div>
          <div v-else class="mt-1"><strong>Horarios:</strong> Todo el día</div>
        </div>
      </v-alert>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-modal-coupon-schedule {
    .section {
      @apply tw-mb-4;

      .section-header {
        @apply tw-flex tw-justify-between tw-items-center tw-mb-3;
      }

      .section-title {
        @apply tw-text-base tw-font-medium tw-flex tw-items-center tw-mb-0;
      }
    }

    .quick-actions {
      @apply tw-flex tw-flex-wrap;
    }

    .days-grid {
      @apply tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-x-4;
    }

    .time-ranges {
      @apply tw-space-y-2;

      .time-range-row {
        @apply tw-items-center;
      }
    }

    .schedule-preview {
      @apply tw-text-sm;
    }
  }
</style>
